import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fmt } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import OperationDialog from "@/components/OperationDialog";
import { Inbox, Droplets, PackageCheck, Network, ArrowRight } from "lucide-react";

function Stage({ title, icon: Icon, iconClass, rows, tone, testid }) {
  const color = tone === "amber" ? "text-amber-700" : tone === "blue" ? "text-blue-700" : "text-emerald-700";
  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white p-4 flex-1 min-w-[150px]" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-5 w-5 ${iconClass}`} strokeWidth={2.3} />
        <h3 className="font-display font-bold text-sm uppercase tracking-tight text-slate-900">{title}</h3>
      </div>
      <div className="space-y-1">
        {(rows || []).map((r) => (
          <div key={r.type} className="flex items-baseline justify-between">
            <span className="text-xs text-slate-500">{r.label}</span>
            <span className={`font-data text-xl font-black ${color}`}>{r.display ?? fmt(r.piles)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DDOWorkspace() {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [op, setOp] = useState(null);

  const allowed = user?.role === "ddo" || user?.role === "admin";

  const { data: ov } = useQuery({
    queryKey: ["logistics-overview"],
    queryFn: async () => (await api.get("/logistics/overview")).data,
    enabled: allowed,
    refetchInterval: 30000,
  });
  const { data: stock } = useQuery({
    queryKey: ["stocks"],
    queryFn: async () => (await api.get("/stocks")).data,
    enabled: allowed,
  });
  const { data: types = [] } = useQuery({ queryKey: ["packaging-types"], queryFn: async () => (await api.get("/packaging-types")).data, enabled: allowed });
  const { data: sites = [] } = useQuery({ queryKey: ["sites"], queryFn: async () => (await api.get("/sites")).data, enabled: allowed });

  if (!allowed) return <Navigate to="/" replace />;

  const ddo = ov?.ddo || {};
  const refresh = () => { qc.invalidateQueries({ queryKey: ["logistics-overview"] }); qc.invalidateQueries({ queryKey: ["stocks"] }); };

  const ACTIONS = [
    { type: "RECEPTION_SALES", label: "Réception des sales", desc: "Arrivée depuis ANNEZIN", icon: Inbox, cls: "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100" },
    { type: "LAVAGE_START", label: "Mise en lavage", desc: "Sales → en cours", icon: Droplets, cls: "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100" },
    { type: "LAVAGE_END", label: "Fin de lavage", desc: "En cours → propres", icon: Droplets, cls: "border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100" },
  ];

  return (
    <AppShell>
      <div className="space-y-5" data-testid="ddo-workspace">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display font-black text-2xl sm:text-3xl text-slate-900 uppercase tracking-tight">Espace DDO — Centre de lavage</h1>
            <p className="text-sm text-slate-500">Réception → mise en lavage → fin de lavage → réservations.</p>
          </div>
          {can("logistics", "view") && (
            <button onClick={() => nav("/logistics")} className="tactile inline-flex items-center gap-1.5 rounded-lg border-2 border-slate-300 px-3 h-11 font-bold text-slate-700 hover:bg-slate-50" data-testid="ddo-open-cockpit">
              <Network className="h-4 w-4" /> Pilotage
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-stretch gap-2">
          <Stage title="En attente de lavage" icon={Droplets} iconClass="text-amber-600" rows={ddo.sales_attente} tone="amber" testid="ddo-stage-attente" />
          <div className="self-center hidden sm:flex"><ArrowRight className="h-5 w-5 text-slate-300" /></div>
          <Stage title="En cours de lavage" icon={Droplets} iconClass="text-blue-600" rows={ddo.en_lavage} tone="blue" testid="ddo-stage-encours" />
          <div className="self-center hidden sm:flex"><ArrowRight className="h-5 w-5 text-slate-300" /></div>
          <Stage title="Propres disponibles" icon={PackageCheck} iconClass="text-emerald-600" rows={ddo.disponibles} tone="emerald" testid="ddo-stage-dispo" />
        </div>

        <div>
          <h2 className="font-display font-extrabold text-lg text-slate-900 mb-2 uppercase tracking-tight">Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {ACTIONS.filter((a) => can("op_lavage", "create") || can("op_reception_sales", "create")).map((a) => {
              const canDo = a.type === "RECEPTION_SALES" ? can("op_reception_sales", "create") : can("op_lavage", "create");
              if (!canDo) return null;
              const Icon = a.icon;
              return (
                <button key={a.type} onClick={() => setOp(a.type)} data-testid={`ddo-action-${a.type}`}
                  className={`tactile rounded-xl border-2 p-3 sm:p-4 text-left flex flex-col gap-1.5 min-h-[92px] ${a.cls}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                  <span className="font-display font-bold leading-tight text-sm sm:text-base">{a.label}</span>
                  <span className="text-xs opacity-70">{a.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        {op && stock && (
          <OperationDialog
            type={op}
            open={!!op}
            onOpenChange={(v) => !v && setOp(null)}
            onDone={refresh}
            matrix={stock.matrix || []}
            types={types}
            sites={sites}
            user={user}
            editMovement={null}
          />
        )}
      </div>
    </AppShell>
  );
}
